import { useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { layoutTree } from "@shared/tree-layout";
import type { SeedTree } from "@shared/api-types.js";
import { Skeleton } from "@web-shared/components/ui/skeleton";
import { webApi } from "@web-shared/lib/web-api";
import { initials, labelColor } from "@/lib/cover";
import { seedPath } from "@/lib/ctas";

const METRICS = { nodeW: 168, nodeH: 52, colGap: 28, rowGap: 30 };
const PAD = 12;
const CHIP_W = 44;
const CHIP_H = 16;
const CHIP_GAP = 3;
const CHIP_MARGIN = 6;

export function useSeedTree(seedId: string) {
  return useQuery({
    queryKey: ["web", "tree", seedId],
    staleTime: 5 * 60_000,
    // ?audio=1 signs each node's master so the remix list can audition one
    // without a second request (workers/src/jams.ts treeForSeed).
    queryFn: () => webApi<SeedTree>(`/seeds/${encodeURIComponent(seedId)}/tree?audio=1`),
  });
}

export function RemixTree({ seedId, inline }: { seedId: string; inline?: boolean }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const goToSeed = useCallback((id: string) => {
    const tab = params.get("tab");
    const search = tab ? `?tab=${tab}` : "";
    navigate(`${seedPath(id)}${search}`);
  }, [navigate, params]);
  const tree = useSeedTree(seedId);
  const scrolledTo = useRef<string | null>(null);
  const laid = useMemo(
    () => (tree.data ? layoutTree(tree.data.nodes, tree.data.jams, METRICS) : null),
    [tree.data],
  );
  const byId = useMemo(() => new Map((tree.data?.nodes ?? []).map((n) => [n.id, n])), [tree.data]);

  if (tree.isPending) return <Skeleton className={inline ? "h-28 w-full" : "h-28 w-full rounded-xl"} />;
  if (tree.isError || !laid || !tree.data) return null;
  if (tree.data.nodes.length < 2) {
    return inline ? <p className="px-4 py-6 text-center text-sm text-muted-foreground">No remixes yet.</p> : null;
  }

  const { nodeW: W, nodeH: H } = METRICS;
  const placed = new Map(laid.nodes.map((p) => [p.id, p]));
  const focusId = tree.data.focusId;
  const focusPos = placed.get(focusId);

  // Invert Y: newest (deepest) at top, root at bottom — like a real tree.
  const FY = (y: number, h: number): number => laid.height - y - h;

  // Group jams by base seed for chip stacking
  const jamsByBase = new Map<string, typeof tree.data.jams>();
  for (const j of tree.data.jams) {
    if (!placed.has(j.baseSeedId)) continue;
    const list = jamsByBase.get(j.baseSeedId) ?? [];
    list.push(j);
    jamsByBase.set(j.baseSeedId, list);
  }

  // Widen SVG if chips extend past the rightmost node
  let maxRight = laid.width;
  for (const [baseId] of jamsByBase) {
    const base = placed.get(baseId);
    if (base) maxRight = Math.max(maxRight, base.x + W + CHIP_MARGIN + CHIP_W);
  }

  const scrollToFocus = (el: HTMLDivElement | null) => {
    if (!el || !focusPos || scrolledTo.current === focusId) return;
    scrolledTo.current = focusId;
    const focusFY = FY(focusPos.y, H);
    el.scrollLeft = Math.max(0, focusPos.x + W / 2 - el.clientWidth / 2);
    if (el.scrollHeight > el.clientHeight) {
      el.scrollTop = Math.max(0, focusFY + H / 2 - el.clientHeight / 2);
    }
  };

  const svgW = maxRight + PAD * 2;
  const svgH = laid.height + PAD * 2;

  const svg = (
    <div
      className="scrollbar-thin overflow-x-auto overflow-y-auto"
      style={inline ? undefined : { maxHeight: 320 }}
      ref={scrollToFocus}
    >
      <svg width={svgW} height={svgH} viewBox={`${-PAD} ${-PAD} ${svgW} ${svgH}`} className="block" role="img" aria-label="Remix tree">
        {/* Edges: parent top → child bottom (inverted) */}
        {laid.edges.map((e) => {
          const a = placed.get(e.from);
          const b = placed.get(e.to);
          if (!a || !b) return null;
          const x1 = a.x + W / 2;
          const y1 = FY(a.y, H);
          const x2 = b.x + W / 2;
          const y2 = FY(b.y, H) + H;
          const mid = (y1 + y2) / 2;
          return (
            <path
              key={`${e.from}-${e.to}`}
              d={`M${x1},${y1} C${x1},${mid} ${x2},${mid} ${x2},${y2}`}
              fill="none"
              stroke="var(--muted-foreground)"
              strokeOpacity={0.5}
              strokeWidth={1.5}
            />
          );
        })}

        {/* Jam chips: small pills beside the base node they were forked from */}
        {tree.data.jams.map((jam) => {
          const base = placed.get(jam.baseSeedId);
          if (!base) return null;
          const siblings = jamsByBase.get(jam.baseSeedId) ?? [];
          const idx = siblings.indexOf(jam);
          const by = FY(base.y, H);
          const cx = base.x + W + CHIP_MARGIN;
          const cy = by + idx * (CHIP_H + CHIP_GAP);
          const lineY = cy + CHIP_H / 2;
          const commits = jam.commitCount || 0;
          const label = commits === 1 ? "1 player" : `${commits} players`;
          return (
            <g key={jam.id}>
              <line
                x1={base.x + W} y1={lineY}
                x2={cx} y2={lineY}
                stroke="var(--primary)" strokeWidth={1} strokeDasharray="3 2" opacity={0.4}
              />
              <g>
                <title>{jam.title} — {label}</title>
                <rect
                  x={cx} y={cy} width={CHIP_W} height={CHIP_H} rx={8}
                  fill="color-mix(in srgb, var(--primary) 10%, var(--card))"
                  stroke="var(--primary)" strokeWidth={1} strokeDasharray="3 2"
                />
                {/* Broadcast icon */}
                <g transform={`translate(${cx + 5},${cy + CHIP_H / 2 - 5}) scale(0.42)`}>
                  <circle cx="12" cy="12" r="2" fill="none" stroke="var(--primary)" strokeWidth="2.5" />
                  <path d="M16.24 7.76a6 6 0 0 1 0 8.49" fill="none" stroke="var(--primary)" strokeWidth="2.5" strokeLinecap="round" />
                  <path d="M7.76 16.24a6 6 0 0 1 0-8.49" fill="none" stroke="var(--primary)" strokeWidth="2.5" strokeLinecap="round" />
                </g>
                <text x={cx + 20} y={cy + CHIP_H / 2 + 3.5} fontSize={9} fontWeight={600} fill="var(--primary)">
                  {commits}
                </text>
              </g>
            </g>
          );
        })}

        {/* Nodes */}
        {laid.nodes.map((p) => {
          const node = byId.get(p.id);
          if (!node) return null;
          const focus = node.id === focusId;
          const fy = FY(p.y, H);
          const hasNote = !!node.note;
          const headline = hasNote ? truncate(node.note!, 20) : truncate(node.title, 20);
          const subline = hasNote
            ? truncate(node.title, 18)
            : `v${node.version}${node.childCount > 0 ? ` · ${node.childCount} ↓` : ""}`;
          return (
            <g
              key={node.id}
              transform={`translate(${p.x},${fy})`}
              className="cursor-pointer"
              role="link"
              tabIndex={0}
              aria-label={`${node.title} by ${node.producer.username}`}
              onClick={() => goToSeed(node.id)}
              onKeyDown={(e) => { if (e.key === "Enter") goToSeed(node.id); }}
            >
              <rect
                width={W}
                height={H}
                rx={10}
                fill={focus ? "color-mix(in srgb, var(--primary) 16%, var(--card))" : "var(--background)"}
                stroke={focus ? "var(--primary)" : "var(--border)"}
                strokeWidth={focus ? 1.5 : 1}
              />
              {/* Producer avatar */}
              <circle cx={22} cy={H / 2} r={12} fill={labelColor(node.id)} fillOpacity={0.22} />
              <text x={22} y={H / 2 + 3.5} fontSize={9} fontWeight={700} textAnchor="middle" fill={labelColor(node.id)}>
                {initials(node.producer.username)}
              </text>
              {/* Headline + subline */}
              <text x={42} y={21} fontSize={11} fontWeight={600} fill="var(--foreground)">
                {headline}
              </text>
              <text x={42} y={36} fontSize={10} fill="var(--muted-foreground)">
                {subline}
              </text>
              {/* "from jam" tag */}
              {node.jamId && (
                <text x={42} y={48} fontSize={8} fontWeight={600} fill="var(--primary)" opacity={0.8}>
                  from jam
                </text>
              )}
              {/* Jam tip marker */}
              {p.tipOf.length > 0 && (
                <circle cx={W - 14} cy={14} r={4} fill="var(--primary)" stroke="var(--card)" strokeWidth={1.5}>
                  <title>tip of {p.tipOf.length} jam{p.tipOf.length === 1 ? "" : "s"}</title>
                </circle>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );

  if (inline) return svg;

  return (
    <section id="tree" className="rounded-xl border border-border bg-card">
      <h2 className="border-b border-border px-4 py-2.5 text-sm font-medium">
        Remix tree <span className="text-muted-foreground">· {tree.data.nodes.length} versions</span>
      </h2>
      {svg}
      <div className="flex flex-wrap items-center gap-4 border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <svg width="18" height="6"><line x1="0" y1="3" x2="18" y2="3" stroke="var(--muted-foreground)" strokeWidth="1.5" /></svg>
          parent → child
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="18" height="6"><line x1="0" y1="3" x2="18" y2="3" stroke="var(--primary)" strokeWidth="1" strokeDasharray="3 2" /></svg>
          jam
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="8" height="8"><circle cx="4" cy="4" r="3.5" fill="var(--primary)" /></svg>
          tip
        </span>
        {tree.data.truncated && <span className="text-destructive">tree truncated at server cap</span>}
      </div>
    </section>
  );
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}
